import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Crown,
  Search,
  Filter,
  Plus,
  X,
  Send,
  MapPin,
  Calendar,
  ImageIcon,
  Users,
  ChevronLeft,
  ChevronRight,
  Eye,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import { useNavigate } from "react-router-dom";
import { fetchCategories } from "../../requests/Categories/CategoryService";
import Loader from "../../components/Loader";
import { regions } from "../../utils/regions";
import RenderRecentDealStatus from "../../components/StatusBadge";
import Config from "../../Config";
import axios from "axios";
import { toast } from "react-toastify";

const LocationModal = ({ isOpen, onClose, locations }) => {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="relative p-6 m-4 w-full max-w-md bg-white rounded-xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 text-gray-400 rounded-full hover:text-gray-600 hover:bg-gray-100"
        >
          <X className="w-5 h-5" />
        </button>

        <h3 className="mb-4 text-lg font-semibold text-gray-900">
          All Locations
        </h3>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {locations.map((loc, index) => (
            <div
              key={index}
              className="flex gap-3 items-center p-3 bg-gray-50 rounded-lg"
            >
              <MapPin className="flex-shrink-0 w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-gray-900">{loc.location}</p>
                <p className="text-sm text-gray-500">{loc.region}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};

const StatusToggle = ({ isActive, onToggle, isLoading, dealTitle }) => {
  return (
    <div className="inline-flex relative items-center">
      <button
        onClick={onToggle}
        disabled={isLoading}
        className={`
          relative w-11 h-6 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500
          ${isActive ? "bg-amber-600" : "bg-gray-200"}
          ${isLoading ? "opacity-50 cursor-wait" : "cursor-pointer"}
        `}
        title={`${isActive ? "Deactivate" : "Activate"} ${dealTitle}`}
      >
        <span
          className={`
            absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-300 ease-in-out
            ${isActive ? "translate-x-5" : "translate-x-0"}
            ${isLoading ? "animate-pulse" : ""}
          `}
        />
      </button>
      {isLoading && (
        <div className="flex absolute -right-6 items-center">
          <div className="w-4 h-4 rounded-full border-2 border-amber-600 animate-spin border-t-transparent" />
        </div>
      )}
    </div>
  );
};

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  isLoading,
}) => {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="relative p-6 m-4 w-full max-w-md bg-white rounded-xl shadow-xl"
      >
        <h3 className="mb-2 text-lg font-semibold text-gray-900">{title}</h3>
        <p className="mb-6 text-gray-600">{message}</p>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex gap-2 items-center px-4 py-2 text-white bg-amber-600 rounded-lg transition-colors hover:bg-amber-700"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent" />
                Processing...
              </>
            ) : (
              "Confirm"
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const PremiumDeals = ({ setPremiumDeal }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    region: "",
    category: "",
    priceRange: [0, 1000],
    dateRange: {
      start: "",
      end: "",
    },
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dealsPerPage] = useState(9);
  const [deals, setDeals] = useState([]);
  const [totalDeals, setTotalDeals] = useState(0);
  const [isLoadingDeals, setIsLoadingDeals] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState(null);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [statusLoading, setStatusLoading] = useState({});
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    dealSlug: null,
    currentStatus: null,
    dealTitle: "",
  });

  // Helper function to format location display
  const formatLocation = (locationArray) => {
    if (!locationArray || locationArray.length === 0)
      return "Location not specified";

    const firstLocation = locationArray[0];
    if (firstLocation.region === "all" && firstLocation.location === "all") {
      return "Nationwide";
    }

    const additionalLocations =
      locationArray.length > 1 ? ` +${locationArray.length - 1}` : "";

    return `${firstLocation.location}, ${firstLocation.region}${additionalLocations}`;
  };

  //Todo => Fetching Categories
  useEffect(() => {
    const fetchActiveCategories = async () => {
      fetchCategories(setCategories, setLoading, setError);
    };
    fetchActiveCategories();
  }, []);

  const handleViewDeal = (deal) => {
    setPremiumDeal(deal);
    navigate(`/premium-deals/${deal.premium_deal_slug}`);
  };

  // Fetch deals with pagination and filters
  useEffect(() => {
    const fetchDeals = async () => {
      setIsLoadingDeals(true);
      try {
        const token = localStorage.getItem("auth_token");
        const response = await axios.get(`${Config.baseUrl}/only/deals`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = response.data;
        if (data.data.status_code === "999") {
          const allDeals = data.data.data;
          let filteredResults = allDeals;

          // Apply search filter
          if (searchTerm) {
            filteredResults = filteredResults.filter(
              (deal) =>
                deal.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                deal.description
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase()) ||
                (deal.location &&
                  deal.location.some(
                    (loc) =>
                      loc.region
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                      loc.location
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                  ))
            );
          }

          // Apply region filter
          if (filters.region) {
            filteredResults = filteredResults.filter((deal) => {
              if (!deal.location || deal.location.length === 0) return false;

              // Check if it's a nationwide deal
              if (
                deal.location[0].region === "all" &&
                deal.location[0].location === "all"
              ) {
                return true;
              }

              // Check if any location matches the filter region
              return deal.location.some((loc) => loc.region === filters.region);
            });
          }

          // Apply category filter
          if (filters.category) {
            filteredResults = filteredResults.filter(
              (deal) => deal.category === filters.category
            );
          }

          // Apply date range filter
          if (filters.dateRange.start || filters.dateRange.end) {
            filteredResults = filteredResults.filter((deal) => {
              const dealDate = new Date(deal.expiryDate);
              const startDate = filters.dateRange.start
                ? new Date(filters.dateRange.start)
                : null;
              const endDate = filters.dateRange.end
                ? new Date(filters.dateRange.end)
                : null;

              if (startDate && endDate) {
                return dealDate >= startDate && dealDate <= endDate;
              } else if (startDate) {
                return dealDate >= startDate;
              } else if (endDate) {
                return dealDate <= endDate;
              }
              return true;
            });
          }

          // Calculate pagination
          const totalFilteredDeals = filteredResults.length;
          const startIndex = (currentPage - 1) * dealsPerPage;
          const endIndex = startIndex + dealsPerPage;
          const paginatedDeals = filteredResults.slice(startIndex, endIndex);

          setDeals(paginatedDeals);
          setTotalDeals(totalFilteredDeals);
          setTotalPages(Math.ceil(totalFilteredDeals / dealsPerPage));
          setIsLoadingDeals(false);
        } else {
          setError(data.data.reason);
          setIsLoadingDeals(false);
        }
      } catch (err) {
        console.error("Error fetching deals:", err);
        setError("Failed to load premium deals");
        setIsLoadingDeals(false);
      }
    };

    fetchDeals();
  }, [currentPage, dealsPerPage, filters, searchTerm]);

  console.log("deals", deals);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  const filterPanelVariants = {
    hidden: { x: "100%", opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 30 },
    },
    exit: {
      x: "100%",
      opacity: 0,
      transition: { type: "spring", stiffness: 300, damping: 30 },
    },
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of the page
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Placeholder image configuration
  const PLACEHOLDER_COLORS = [
    "bg-amber-100",
    "bg-rose-100",
    "bg-blue-100",
    "bg-green-100",
    "bg-purple-100",
  ];

  const getPlaceholderColor = (id) => {
    return PLACEHOLDER_COLORS[id % PLACEHOLDER_COLORS.length];
  };

  const handleStatusToggle = async (dealSlug, currentStatus, dealTitle) => {
    setConfirmModal({
      isOpen: true,
      dealSlug,
      currentStatus,
      dealTitle,
    });
  };

  const handleStatusConfirm = async () => {
    const { dealSlug, currentStatus } = confirmModal;

    try {
      setStatusLoading((prev) => ({ ...prev, [dealSlug]: true }));

      const token = localStorage.getItem("auth_token");
      const endpoint =
        currentStatus === "active"
          ? `${Config.baseUrl}/deactivate/deal`
          : `${Config.baseUrl}/activate/deal/${dealSlug}`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response?.data;

      if (data.data.status_code === "000") {
        setDeals((prevDeals) =>
          prevDeals.map((deal) =>
            deal.premium_deal_slug === dealSlug
              ? {
                  ...deal,
                  status: currentStatus === "active" ? "inactive" : "active",
                }
              : deal
          )
        );
        toast.success("Status updated successfully");
      } else {
        toast.error(data.data.reason || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating deal status:", error);
      toast.error(error.message || "Failed to update deal status");
    } finally {
      setStatusLoading((prev) => ({ ...prev, [dealSlug]: false }));
      setConfirmModal({
        isOpen: false,
        dealSlug: null,
        currentStatus: null,
        dealTitle: "",
      });
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="relative">
      <Navbar page="Premium Deals" />
      <div className="p-6 min-h-screen bg-slate-50">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="space-y-8"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
            <div className="flex gap-3 items-center">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Crown className="w-6 h-6 text-amber-600" />
              </div>
              <h1 className="text-xl font-bold text-gray-800 sm:text-2xl">
                Premium Deals
              </h1>
            </div>
            <div className="flex flex-col gap-4 items-start sm:flex-row sm:items-center">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate("/create-premium-deal")}
                className="flex gap-2 justify-center items-center px-4 py-2 w-full font-medium text-white bg-amber-600 rounded-lg transition-colors hover:bg-amber-700 sm:w-auto sm:justify-start"
              >
                <Plus className="w-4 h-4" />
                Create Premium Deal
              </motion.button>
              <div className="w-full text-sm text-center text-gray-500 sm:w-auto sm:text-left">
                {totalDeals > 0 ? (
                  <span>
                    Showing {(currentPage - 1) * dealsPerPage + 1}-
                    {Math.min(currentPage * dealsPerPage, totalDeals)} of{" "}
                    {totalDeals} deals
                  </span>
                ) : (
                  <span>No deals found</span>
                )}
              </div>
            </div>
          </div>

          <motion.div
            variants={itemVariants}
            className="flex flex-col gap-4 sm:flex-row"
          >
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search premium deals..."
                className="py-2 pr-4 pl-10 w-full bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
                onChange={handleSearch}
                value={searchTerm}
              />
            </div>
            <button
              onClick={() => setIsFilterOpen(true)}
              className="flex gap-2 justify-center items-center px-4 py-2 w-full text-gray-700 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 sm:justify-start sm:w-auto"
            >
              <Filter className="w-5 h-5" />
              Filters
            </button>
          </motion.div>

          {isLoadingDeals ? (
            <div className="flex justify-center items-center py-12">
              <div className="w-12 h-12 rounded-full border-b-2 border-amber-600 animate-spin"></div>
            </div>
          ) : deals.length > 0 ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {deals.map((deal) => (
                <motion.div
                  key={deal.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="overflow-hidden bg-white rounded-xl border border-gray-100 shadow-sm transition-all group hover:shadow-md"
                >
                  {/* Deal Image Section */}
                  <div className="relative aspect-[16/9] overflow-hidden">
                    {deal.deal_image ? (
                      <img
                        src={deal.deal_image}
                        alt={deal.title}
                        className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className={`absolute inset-0 ${
                        !deal.deal_image ? "flex" : "hidden"
                      } flex-col justify-center items-center ${getPlaceholderColor(
                        deal.id
                      )}`}
                    >
                      <ImageIcon className="mb-2 w-8 h-8 text-gray-400" />
                      <p className="px-4 text-sm text-center text-gray-500 line-clamp-2">
                        {deal.title || "Premium Deal"}
                      </p>
                    </div>
                    <div className="flex absolute top-3 left-3 gap-2">
                      <span className="inline-flex gap-1 items-center px-3 py-1 text-sm font-medium text-amber-700 bg-amber-100 rounded-full shadow-sm">
                        <Crown className="w-4 h-4" />
                        Premium
                      </span>
                    </div>
                    <div className="flex absolute top-3 right-3 gap-2">
                      <RenderRecentDealStatus status={deal.status} />
                    </div>
                  </div>

                  {/* Deal Content */}
                  <div className="p-4">
                    <div className="space-y-4">
                      {/* Title and Location */}
                      <div>
                        <h3 className="font-semibold text-gray-900 transition-colors text-md line-clamp-2 group-hover:text-amber-600">
                          {deal.title}
                        </h3>
                        {deal.location && deal.location.length > 0 && (
                          <div
                            className={`flex gap-2 items-center mt-1 text-gray-500 ${
                              deal.location.length > 1
                                ? "cursor-pointer hover:text-amber-600"
                                : ""
                            }`}
                            onClick={() => {
                              if (deal.location.length > 1) {
                                setSelectedLocations(deal.location);
                                setIsLocationModalOpen(true);
                              }
                            }}
                          >
                            <MapPin className="w-4 h-4" />
                            <span className="text-sm">
                              {formatLocation(deal.location)}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Pricing Section */}
                      <div className="flex justify-between items-end">
                        <div>
                          <div className="flex gap-2 items-baseline">
                            <span className="text-2xl font-bold text-amber-600">
                              ₵{deal.discounted_price?.toLocaleString()}
                            </span>
                            <span className="text-sm text-gray-500 line-through">
                              ₵{deal.original_price?.toLocaleString()}
                            </span>
                          </div>
                          <div className="mt-1 text-sm text-green-600">
                            Save{" "}
                            {Math.round(
                              ((deal.original_price - deal.discounted_price) /
                                deal.original_price) *
                                100
                            )}
                            %
                          </div>
                        </div>
                        <div className="flex gap-1 items-center px-2 py-1 text-sm text-amber-700 bg-amber-50 rounded-full">
                          <Users className="w-3 h-3" />
                          <span className="font-bold">
                            {deal.subscriber_count?.toLocaleString()}{" "}
                            <span className="text-xs font-medium">
                              Subscribers
                            </span>
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2 pt-4 mt-4 border-t border-gray-100">
                        <div className="flex gap-2 items-center">
                          <StatusToggle
                            isActive={deal.status?.toLowerCase() === "active"}
                            onToggle={() =>
                              handleStatusToggle(
                                deal.premium_deal_slug,
                                deal.status,
                                deal.title
                              )
                            }
                            isLoading={statusLoading[deal.premium_deal_slug]}
                            dealTitle={deal.title}
                          />
                          <span className="text-sm text-gray-600">
                            {deal.status?.toLowerCase() === "active"
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </div>
                        <motion.button
                          disabled={deal.status?.toLowerCase() !== "active"}
                          whileHover={
                            deal.status?.toLowerCase() === "active"
                              ? { scale: 1.02 }
                              : {}
                          }
                          whileTap={
                            deal.status?.toLowerCase() === "active"
                              ? { scale: 0.98 }
                              : {}
                          }
                          onClick={() =>
                            navigate(
                              `/premium-deals/${deal.premium_deal_slug}/blast`
                            )
                          }
                          className={`flex gap-1 justify-center items-center px-3 py-2 rounded-lg transition-colors ${
                            deal.status?.toLowerCase() === "active"
                              ? "text-amber-700 bg-amber-50 hover:bg-amber-100"
                              : "text-gray-400 bg-gray-100 cursor-not-allowed"
                          }`}
                        >
                          <Send className="w-4 h-4" />
                          <span className="text-sm font-medium">Blast</span>
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleViewDeal(deal)}
                          className="flex gap-1 justify-center items-center px-3 py-2 text-white bg-amber-600 rounded-lg transition-colors hover:bg-amber-700"
                        >
                          <Eye className="w-4 h-4" />
                          <span className="text-sm font-medium">View</span>
                        </motion.button>
                      </div>

                      {/* Expiry Date */}
                      <div className="flex gap-2 items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span>
                          Expires {new Date(deal.end_date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="flex justify-center items-center py-16">
              <div className="p-8 text-center bg-white rounded-lg shadow-sm">
                <ImageIcon className="mx-auto mb-4 w-16 h-16 text-gray-300" />
                <h3 className="mb-2 text-xl font-semibold text-gray-800">
                  No Premium Deals Found
                </h3>
                <p className="text-gray-500">
                  Try adjusting your search or filters to find deals.
                </p>
              </div>
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-8">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg border ${
                    currentPage === 1
                      ? "text-gray-400 border-gray-200 cursor-not-allowed"
                      : "text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="flex gap-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`flex justify-center items-center w-10 h-10 text-sm border rounded-lg ${
                          currentPage === pageNum
                            ? "text-white bg-amber-600 border-amber-600"
                            : "text-gray-700 border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <>
                      {currentPage < totalPages - 3 && (
                        <span className="px-2 text-gray-500">...</span>
                      )}
                      <button
                        onClick={() => handlePageChange(totalPages)}
                        className="flex justify-center items-center w-10 h-10 text-sm text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg border ${
                    currentPage === totalPages
                      ? "text-gray-400 border-gray-200 cursor-not-allowed"
                      : "text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Filter Panel */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={filterPanelVariants}
            className="overflow-y-auto fixed top-0 right-0 z-50 w-96 h-full bg-white shadow-xl"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Filters</h2>
                <button
                  onClick={() => setIsFilterOpen(false)}
                  className="p-2 rounded-full hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Region Filter */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Region
                  </label>
                  <select
                    value={filters.region}
                    onChange={(e) =>
                      handleFilterChange("region", e.target.value)
                    }
                    className="p-2 w-full rounded-lg border border-gray-200 focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">All Regions</option>
                    {regions.map((region) => (
                      <option key={region.id} value={region.value}>
                        {region.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Category Filter */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Category
                  </label>
                  <select
                    value={filters.category}
                    onChange={(e) =>
                      handleFilterChange("category", e.target.value)
                    }
                    className="p-2 w-full rounded-lg border border-gray-200 focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">All Categories</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                    {/* Add other categories */}
                  </select>
                </div>

                {/* Date Range Filter */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Date Range
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={filters.dateRange.start}
                      onChange={(e) =>
                        handleFilterChange("dateRange", {
                          ...filters.dateRange,
                          start: e.target.value,
                        })
                      }
                      className="p-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-amber-500"
                    />
                    <input
                      type="date"
                      value={filters.dateRange.end}
                      onChange={(e) =>
                        handleFilterChange("dateRange", {
                          ...filters.dateRange,
                          end: e.target.value,
                        })
                      }
                      className="p-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>

                {/* Apply Filters Button */}
                <button
                  onClick={() => {
                    setCurrentPage(1); // Reset to first page when applying filters
                    setIsFilterOpen(false);
                  }}
                  className="py-2 w-full text-white bg-amber-600 rounded-lg hover:bg-amber-700"
                >
                  Apply Filters
                </button>

                {/* Reset Filters Button */}
                <button
                  onClick={() => {
                    setFilters({
                      region: "",
                      category: "",
                      priceRange: [0, 1000],
                      dateRange: {
                        start: "",
                        end: "",
                      },
                    });
                    setCurrentPage(1);
                  }}
                  className="py-2 w-full text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Reset Filters
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add the LocationModal component */}
      <AnimatePresence>
        {isLocationModalOpen && (
          <LocationModal
            isOpen={isLocationModalOpen}
            onClose={() => setIsLocationModalOpen(false)}
            locations={selectedLocations}
          />
        )}
      </AnimatePresence>

      {/* Add the ConfirmationModal */}
      <AnimatePresence>
        {confirmModal.isOpen && (
          <ConfirmationModal
            isOpen={confirmModal.isOpen}
            onClose={() =>
              setConfirmModal({
                isOpen: false,
                dealSlug: null,
                currentStatus: null,
                dealTitle: "",
              })
            }
            onConfirm={handleStatusConfirm}
            title="Confirm Status Change"
            message={`Are you sure you want to ${
              confirmModal.currentStatus === "active"
                ? "deactivate"
                : "activate"
            } "${confirmModal.dealTitle}"?`}
            isLoading={statusLoading[confirmModal.dealSlug]}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PremiumDeals;
